import { normalizedHost, parseHttpUrl } from "@/blocks/ProductGallery/media-url";
import { createVideoPreviewController } from "@/blocks/ProductGallery/video-preview";
import { CarouselSlider } from "@/components/carousel-slider/carousel-slider.component";
const SCROLL_LOCK_ATTR = "data-product-gallery-scroll-lock";
function setPageScrollLocked(locked) {
    document.documentElement.toggleAttribute(SCROLL_LOCK_ATTR, locked);
}
/** Rewrites a known YouTube/Vimeo page link to its player URL; any other embed URL is used as-is. */
function toEmbedUrl(parsed) {
    const url = parsed.toString();
    const host = normalizedHost(parsed);
    if (host === "youtu.be") {
        const id = parsed.pathname.split("/").filter(Boolean)[0];
        return id ? `https://www.youtube.com/embed/${encodeURIComponent(id)}` : url;
    }
    if (host === "youtube.com" || host.endsWith(".youtube.com")) {
        if (parsed.pathname.startsWith("/embed/")) {
            return url;
        }
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${encodeURIComponent(id)}` : url;
    }
    if (host === "vimeo.com") {
        const id = parsed.pathname.split("/").filter(Boolean)[0];
        return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : url;
    }
    return url;
}
function withAutoplay(parsed) {
    const embed = toEmbedUrl(parsed);
    if (embed.includes("autoplay=0")) {
        return embed.replace("autoplay=0", "autoplay=1");
    }
    if (embed.includes("autoplay=")) {
        return embed;
    }
    const separator = embed.includes("?") ? "&" : "?";
    return `${embed}${separator}autoplay=1`;
}
function clearPlayer(slide) {
    slide.querySelectorAll("iframe, video").forEach((node) => node.remove());
    const poster = slide.querySelector("[data-lightbox-poster]");
    if (poster) {
        poster.hidden = false;
    }
}
function mountPlayer(slide) {
    const url = slide.getAttribute("data-video-url");
    const media = slide.querySelector("[data-lightbox-media]");
    const parsedUrl = url ? parseHttpUrl(url) : null;
    if (!parsedUrl || !media || media.querySelector("iframe, video")) {
        return;
    }
    const poster = slide.querySelector("[data-lightbox-poster]");
    if (poster) {
        poster.hidden = true;
    }
    const title = slide.getAttribute("data-alt") || "Product video";
    // The server classifies embed vs. file (PdpUrlResolver.IsEmbedVideoUrl); the client only follows it.
    if (slide.getAttribute("data-video-kind") === "embed") {
        const iframe = document.createElement("iframe");
        iframe.className = "size-full border-0 bg-white object-contain";
        iframe.src = withAutoplay(parsedUrl);
        iframe.title = title;
        iframe.allow =
            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
        iframe.allowFullscreen = true;
        media.appendChild(iframe);
        return;
    }
    const video = document.createElement("video");
    video.className = "size-full border-0 bg-white object-contain";
    video.src = parsedUrl.toString();
    video.controls = true;
    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute("controlsList", "nodownload");
    media.appendChild(video);
    video.play().catch(() => undefined);
}
function getSwiper(carouselRoot) {
    const swiperElement = carouselRoot?.querySelector(".swiper");
    if (!swiperElement) {
        return undefined;
    }
    return swiperElement.swiper;
}
function parseSlideIndex(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}
export const ProductGallery = (element) => {
    const slides = Array.from(element.querySelectorAll("[data-gallery-slide]"));
    const thumbs = Array.from(element.querySelectorAll("[data-gallery-thumb]"));
    const status = element.querySelector("[data-gallery-status]");
    const carouselRoot = element.querySelector("[data-pdp-carousel]");
    const lightbox = element.querySelector("[data-gallery-lightbox]");
    const lightboxSlides = Array.from(element.querySelectorAll("[data-lightbox-slide]"));
    const lightboxThumbs = Array.from(element.querySelectorAll("[data-lightbox-thumb]"));
    const counter = element.querySelector("[data-lightbox-counter]");
    if (slides.length === 0) {
        return;
    }
    const slideCount = slides.length;
    const normalizeIndex = (index) => {
        if (!Number.isFinite(index) || slideCount === 0) {
            return 0;
        }
        return Math.max(0, Math.min(slideCount - 1, Math.trunc(index)));
    };
    const cleanupCarousel = carouselRoot
        ? CarouselSlider(carouselRoot, {
            showArrows: slideCount > 1,
            showDots: false,
            infinite: false,
            slidesToShowMobile: 1,
            slidesToShowDesktop: 1,
            effect: "slide",
        })
        : undefined;
    const swiper = getSwiper(carouselRoot);
    let current = normalizeIndex(swiper?.realIndex ?? 0);
    let opener = null;
    const previews = createVideoPreviewController(slides, carouselRoot ?? element);
    const syncUi = (index) => {
        current = normalizeIndex(index);
        previews.setActive(current);
        [...thumbs, ...lightboxThumbs].forEach((thumb) => {
            const thumbIndex = parseSlideIndex(thumb.getAttribute("data-gallery-index"), -1);
            thumb.setAttribute("aria-current", thumbIndex === current ? "true" : "false");
        });
        // One slide is visible at a time: keep controls in the other slides out of the tab order so focus
        // can never land in an off-screen slide (which would scroll the overflow-hidden swiper container).
        slides.forEach((slide, slideIndex) => {
            slide.toggleAttribute("inert", slideIndex !== current);
        });
        lightboxSlides.forEach((slide, slideIndex) => {
            const active = slideIndex === current;
            slide.hidden = !active;
            if (!active) {
                clearPlayer(slide);
            }
        });
        const kind = slides[current]?.getAttribute("data-media-kind") === "video" ? "Video" : "Image";
        if (status) {
            status.textContent = `${kind} ${current + 1} of ${slideCount}`;
        }
        if (counter) {
            counter.textContent = `${current + 1} / ${slideCount}`;
        }
        if (lightbox?.open) {
            const activeSlide = lightboxSlides[current];
            if (activeSlide) {
                mountPlayer(activeSlide);
            }
        }
    };
    const goTo = (index) => {
        const nextIndex = normalizeIndex(index);
        if (swiper && swiper.realIndex !== nextIndex) {
            swiper.slideTo(nextIndex);
        }
        syncUi(nextIndex);
    };
    const openLightbox = (index) => {
        if (!lightbox) {
            return;
        }
        opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        previews.setLightboxOpen(true);
        const targetIndex = normalizeIndex(index);
        goTo(targetIndex);
        if (!lightbox.open) {
            lightbox.showModal();
            setPageScrollLocked(true);
        }
        const activeSlide = lightboxSlides[targetIndex];
        if (activeSlide) {
            mountPlayer(activeSlide);
        }
        lightbox.querySelector("[data-lightbox-close]")?.focus();
    };
    const onClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        if (target.closest("[data-lightbox-close]")) {
            event.preventDefault();
            lightbox?.close();
            return;
        }
        if (target.closest("[data-lightbox-prev]")) {
            event.preventDefault();
            if (current > 0) {
                goTo(current - 1);
            }
            return;
        }
        if (target.closest("[data-lightbox-next]")) {
            event.preventDefault();
            if (current < slideCount - 1) {
                goTo(current + 1);
            }
            return;
        }
        const openerControl = target.closest("[data-gallery-open]");
        if (openerControl) {
            const index = parseSlideIndex(openerControl.getAttribute("data-gallery-index"), current);
            event.preventDefault();
            openLightbox(index);
            return;
        }
        const thumb = target.closest("[data-gallery-thumb], [data-lightbox-thumb]");
        if (!thumb) {
            return;
        }
        const index = parseSlideIndex(thumb.getAttribute("data-gallery-index"), current);
        event.preventDefault();
        goTo(index);
    };
    const onLightboxClose = () => {
        setPageScrollLocked(false);
        previews.setLightboxOpen(false);
        lightboxSlides.forEach(clearPlayer);
        // The opener may now sit in an inert (non-active) slide; fall back to the active slide's open control.
        const focusTarget = opener && !opener.closest("[inert]")
            ? opener
            : slides[current]?.querySelector("[data-gallery-open]");
        focusTarget?.focus();
        opener = null;
    };
    const onLightboxClick = (event) => {
        if (event.target === lightbox) {
            lightbox?.close();
        }
    };
    const onSlideChange = () => {
        if (!swiper) {
            return;
        }
        syncUi(swiper.realIndex);
    };
    element.addEventListener("click", onClick);
    lightbox?.addEventListener("close", onLightboxClose);
    lightbox?.addEventListener("click", onLightboxClick);
    swiper?.on("slideChange", onSlideChange);
    syncUi(current);
    return () => {
        element.removeEventListener("click", onClick);
        lightbox?.removeEventListener("close", onLightboxClose);
        lightbox?.removeEventListener("click", onLightboxClick);
        swiper?.off("slideChange", onSlideChange);
        cleanupCarousel?.();
        previews.destroy();
        setPageScrollLocked(false);
        if (lightbox?.open) {
            lightbox.close();
        }
    };
};
